import {useCallback} from 'react';
import {
  useCreateEntryManagement,
  useCreateStep,
  useGetEntryManagements,
} from '../../core/repositories/mypageEntryManagementsRepository';
import {
  ENTRY_SOURCE_TYPE_MANUAL,
  type StepCreationTriggerType,
} from '../../features/entryManagements/constants/entrySourceTypes';
import {showErrorSnackBar, showSuccessSnackBar} from '../components/SnackBar';

type CreateEntryManagementParams = {
  companyId: number;
  companyName: string;
  /** 募集情報のタイトル（stepNameとして使用） */
  entryTitle?: string;
  /** 募集情報の締切日（stepのdeadlineとして使用） */
  deadline?: string;
  /** ステップ作成のトリガーとなったアクションの種別 */
  entrySourceType?: StepCreationTriggerType;
};

type UseCreateEntryManagementIfNotExistsOptions = {
  /**
   * trueの場合のみ選考管理の取得クエリを実行する
   * companyIdとcompanyNameが両方存在する場合にtrueを渡すことで、不要なAPI呼び出しを防ぐ
   */
  enabled?: boolean;
};

/**
 * 選考管理が存在しない場合のみ作成するhook
 *
 * - useGetEntryManagementsで既存の選考管理を取得
 * - companyIdが一致するものがない場合のみcreateEntryManagementを実行
 * - 成功時に通知を表示
 */
export const useCreateEntryManagementIfNotExists = ({
  enabled = true,
}: UseCreateEntryManagementIfNotExistsOptions = {}) => {
  const {data: entryManagementsData} = useGetEntryManagements({
    limit: 1000, // 全件取得するために大きな値を設定
    config: {enabled},
  });
  const {mutate: createEntryManagement} = useCreateEntryManagement();
  const {mutate: createStep} = useCreateStep();

  const createIfNotExists = useCallback(
    ({
      companyId,
      companyName,
      entryTitle,
      deadline,
      entrySourceType = ENTRY_SOURCE_TYPE_MANUAL,
    }: CreateEntryManagementParams) => {
      const stepName = entryTitle ?? 'エントリー';

      // 既存の選考管理からcompanyIdが一致するものを探す
      const existingEntry = entryManagementsData?.ok
        ? entryManagementsData.value.entries.find(
            (entry) => entry.companyID === companyId,
          )
        : undefined;

      if (existingEntry) {
        // 同一のentryTitleを持つステップが既に存在する場合は何もしない
        const hasSameStep = existingEntry.latestSteps?.some(
          (step) => step.stepName === stepName,
        );
        if (hasSameStep) {
          return;
        }

        // 選考管理は存在するが、同一ステップがない場合はステップを追加
        createStep(
          {
            companyID: companyId,
            companyName,
            stepName,
            stepOrder: 1,
            entrySourceType,
            deadline,
          },
          {
            onSuccess: (result) => {
              if (result.ok) {
                showSuccessSnackBar({
                  title: `${companyName}にステップを追加しました`,
                });
              } else {
                showErrorSnackBar({title: 'ステップの追加に失敗しました'});
              }
            },
            onError: () => {
              showErrorSnackBar({title: 'ステップの追加に失敗しました'});
            },
          },
        );
        return;
      }

      // 選考管理が存在しない場合は新規作成
      createEntryManagement(
        {
          companyID: companyId,
          companyName,
          steps: [
            {
              stepName,
              stepOrder: 1,
              entrySourceType,
              deadline,
            },
          ],
        },
        {
          onSuccess: (result) => {
            if (result.ok) {
              showSuccessSnackBar({
                title: `${companyName}の選考管理を作成しました`,
              });
            } else {
              showErrorSnackBar({title: '選考管理の作成に失敗しました'});
            }
          },
          onError: () => {
            showErrorSnackBar({title: '選考管理の作成に失敗しました'});
          },
        },
      );
    },
    [entryManagementsData, createEntryManagement, createStep],
  );

  return {createIfNotExists};
};
